import { useEffect, useState, type FormEvent } from "react";

import { Alert, Badge, Button, Card, CardTitle, Input, Label } from "@realroof/ui";

import { api, canSeeAudit } from "../api";
import { rupees } from "../format";
import { useSession } from "../session";
import { Table, td, th } from "../table";

export interface PackAdmin {
  id: string;
  name: string;
  ads: number;
  months: number; // premium per ad
  price: number;
  gst: number;
  total: number;
  per_ad: number;
  active: boolean;
  sort: number;
  starts_at: string | null;
  ends_at: string | null;
  state: "on_sale" | "scheduled" | "ended" | "off";
}

const STATE: Record<PackAdmin["state"], [string, "success" | "info" | "neutral" | "warning"]> = {
  on_sale: ["On sale", "success"],
  scheduled: ["Starts later", "info"],
  ended: ["Ended", "neutral"],
  off: ["Off sale", "warning"],
};

/** Dates are whole days in India: a plan starts at the start of its first day and ends at the end of its last. */
const dayOf = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }) : "");
const startOf = (day: string) => (day ? `${day}T00:00:00+05:30` : null);
const endOf = (day: string) => (day ? `${day}T23:59:59+05:30` : null);

type Draft = { name: string; ads: string; months: string; price: string; active: boolean; sort: string; starts: string; ends: string };
const draftOf = (p: PackAdmin): Draft => ({
  name: p.name,
  ads: String(p.ads),
  months: String(p.months),
  price: String(p.price),
  active: p.active,
  sort: String(p.sort),
  starts: dayOf(p.starts_at),
  ends: dayOf(p.ends_at),
});
const bodyOf = (d: Draft) => ({
  name: d.name.trim(),
  ads: Number(d.ads),
  months: Number(d.months),
  price: Number(d.price),
  active: d.active,
  sort: Number(d.sort),
  starts_at: startOf(d.starts),
  ends_at: endOf(d.ends),
});
const EMPTY: Draft & { id: string } = { id: "", name: "", ads: "1", months: "3", price: "", active: true, sort: "10", starts: "", ends: "" };

/** Plans: ads, premium months per ad and price (excluding GST); a new plan can launch on a date. Changes apply to
 * new orders only and are audited. */
export function Plans() {
  const { me } = useSession();
  const canEdit = canSeeAudit(me?.staff_role); // super admins and admins
  const [packs, setPacks] = useState<PackAdmin[] | null>(null);
  const [edits, setEdits] = useState<Record<string, Draft>>({});
  const [fresh, setFresh] = useState(EMPTY);
  const [message, setMessage] = useState<{ tone: "error" | "success"; text: string } | null>(null);

  const load = () =>
    api<PackAdmin[]>("/admin/packs")
      .then((list) => {
        setPacks(list);
        setEdits(Object.fromEntries(list.map((p) => [p.id, draftOf(p)])));
      })
      .catch((err: Error) => setMessage({ tone: "error", text: err.message }));
  useEffect(() => {
    load();
  }, []);

  async function save(id: string) {
    const d = edits[id];
    setMessage(null);
    try {
      await api(`/admin/packs/${id}`, { method: "PUT", body: bodyOf(d) });
      setMessage({ tone: "success", text: `${d.name} saved. New orders use it; paid orders keep their terms.` });
      await load();
    } catch (err) {
      setMessage({ tone: "error", text: (err as Error).message });
    }
  }

  async function launch(e: FormEvent) {
    e.preventDefault();
    setMessage(null);
    try {
      const made = await api<PackAdmin>("/admin/packs", { method: "POST", body: { id: fresh.id.trim().toLowerCase(), ...bodyOf(fresh) } });
      setMessage({ tone: "success", text: made.state === "scheduled" ? `${made.name} goes on sale on ${fresh.starts}.` : `${made.name} is on sale now.` });
      setFresh(EMPTY);
      await load();
    } catch (err) {
      setMessage({ tone: "error", text: (err as Error).message });
    }
  }

  const set = (id: string, patch: Partial<Draft>) => setEdits((e) => ({ ...e, [id]: { ...e[id], ...patch } }));

  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Plans &amp; pricing</h1>
      <p className="text-sm text-muted-foreground">Prices are before 18% GST. Each plan sets how many months an ad stays premium; credits are valid 12 months.</p>
      {message && <Alert tone={message.tone}>{message.text}</Alert>}
      {packs && (
        <Table>
          <thead>
            <tr>
              <th className={th}>Plan</th>
              <th className={th}>Ads</th>
              <th className={th}>Months</th>
              <th className={th}>Price (₹, excl. GST)</th>
              <th className={th}>With GST</th>
              <th className={th}>Sale dates</th>
              <th className={th}>Status</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {packs.map((p) => {
              const d = edits[p.id];
              const [label, tone] = STATE[p.state] ?? STATE.on_sale;
              return (
                <tr key={p.id}>
                  <td className={td}>
                    {canEdit ? <Input aria-label={`Name of ${p.id}`} value={d.name} onChange={(e) => set(p.id, { name: e.target.value })} /> : p.name}
                    <div className="font-mono text-xs text-muted-foreground">{p.id}</div>
                  </td>
                  <td className={td}>{canEdit ? <Input className="w-20" inputMode="numeric" aria-label={`Ads in ${p.id}`} value={d.ads} onChange={(e) => set(p.id, { ads: e.target.value })} /> : p.ads}</td>
                  <td className={td}>{canEdit ? <Input className="w-20" inputMode="numeric" aria-label={`Months of ${p.id}`} value={d.months} onChange={(e) => set(p.id, { months: e.target.value })} /> : p.months}</td>
                  <td className={td}>{canEdit ? <Input className="w-28" inputMode="decimal" aria-label={`Price of ${p.id}`} value={d.price} onChange={(e) => set(p.id, { price: e.target.value })} /> : rupees(p.price)}</td>
                  <td className={`${td} whitespace-nowrap`}>₹{p.total.toFixed(2)}</td>
                  <td className={td}>
                    {canEdit ? (
                      <div className="flex flex-col gap-1">
                        <Input type="date" aria-label={`Sale starts for ${p.id}`} value={d.starts} onChange={(e) => set(p.id, { starts: e.target.value })} />
                        <Input type="date" aria-label={`Sale ends for ${p.id}`} value={d.ends} onChange={(e) => set(p.id, { ends: e.target.value })} />
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={d.active} onChange={(e) => set(p.id, { active: e.target.checked })} /> Switched on
                        </label>
                      </div>
                    ) : (
                      <span className="text-sm">{[dayOf(p.starts_at) || "now", dayOf(p.ends_at) || "no end"].join(" → ")}</span>
                    )}
                  </td>
                  <td className={td}>
                    <Badge tone={tone}>{label}</Badge>
                  </td>
                  <td className={td}>
                    {canEdit && (
                      <Button size="sm" variant="primary" onClick={() => save(p.id)}>
                        Save
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
      {canEdit ? (
        <Card>
          <form className="flex flex-col gap-3" onSubmit={launch} aria-label="Launch a new plan">
            <CardTitle className="text-base">Launch a new plan</CardTitle>
            <div className="grid gap-3 sm:grid-cols-4">
              <Label>
                Code
                <Input required pattern="[a-z0-9-]{2,20}" placeholder="e.g. monsoon-6m" value={fresh.id} onChange={(e) => setFresh({ ...fresh, id: e.target.value })} />
              </Label>
              <Label className="sm:col-span-3">
                Name
                <Input required value={fresh.name} onChange={(e) => setFresh({ ...fresh, name: e.target.value })} />
              </Label>
              <Label>
                Ads
                <Input required inputMode="numeric" value={fresh.ads} onChange={(e) => setFresh({ ...fresh, ads: e.target.value })} />
              </Label>
              <Label>
                Premium months per ad
                <Input required inputMode="numeric" value={fresh.months} onChange={(e) => setFresh({ ...fresh, months: e.target.value })} />
              </Label>
              <Label>
                Price (₹, excl. GST)
                <Input required inputMode="decimal" value={fresh.price} onChange={(e) => setFresh({ ...fresh, price: e.target.value })} />
              </Label>
              <span />
              <Label>
                On sale from
                <Input type="date" value={fresh.starts} onChange={(e) => setFresh({ ...fresh, starts: e.target.value })} />
              </Label>
              <Label>
                Until (optional)
                <Input type="date" value={fresh.ends} onChange={(e) => setFresh({ ...fresh, ends: e.target.value })} />
              </Label>
            </div>
            <p className="m-0 text-sm text-muted-foreground">Leave &quot;On sale from&quot; empty to sell it at once.</p>
            <Button type="submit" variant="primary" className="self-start">
              Launch plan
            </Button>
          </form>
        </Card>
      ) : (
        <Card className="text-sm text-muted-foreground">Only admins and super admins change prices.</Card>
      )}
      <SellerDetails />
    </section>
  );
}

interface Seller {
  name: string;
  address: string;
  state: string;
  gstin: string;
  sac: string;
}

/** Who receipts and invoices are issued by. With a GSTIN, paid orders get tax invoice numbers. */
export function SellerDetails() {
  const { me } = useSession();
  const canEdit = me?.staff_role === "super_admin";
  const [seller, setSeller] = useState<Seller | null>(null);
  const [message, setMessage] = useState<{ tone: "error" | "success"; text: string } | null>(null);
  useEffect(() => {
    api<Seller>("/admin/settings/seller")
      .then(setSeller)
      .catch((err: Error) => setMessage({ tone: "error", text: err.message }));
  }, []);
  if (!seller) return message ? <Alert tone={message.tone}>{message.text}</Alert> : null;

  async function save() {
    setMessage(null);
    try {
      setSeller(await api<Seller>("/admin/settings/seller", { method: "PUT", body: seller }));
      setMessage({ tone: "success", text: seller!.gstin ? "Saved. Orders paid from now on get tax invoices." : "Saved. Orders paid from now on carry these details." });
    } catch (err) {
      setMessage({ tone: "error", text: (err as Error).message });
    }
  }
  const set = (patch: Partial<Seller>) => setSeller({ ...seller, ...patch });

  return (
    <Card>
      <CardTitle className="text-base">Seller on receipts and invoices</CardTitle>
      <p className="text-sm text-muted-foreground">
        {seller.gstin ? "Tax invoices are issued (CGST + SGST in the same state, IGST elsewhere)." : "No GSTIN yet: buyers get payment receipts. Enter the GSTIN to start tax invoices."}
      </p>
      {message && <Alert tone={message.tone}>{message.text}</Alert>}
      <fieldset disabled={!canEdit} className="grid gap-3 sm:grid-cols-2">
        <Label>
          Legal name
          <Input value={seller.name} onChange={(e) => set({ name: e.target.value })} />
        </Label>
        <Label>
          State
          <Input value={seller.state} onChange={(e) => set({ state: e.target.value })} />
        </Label>
        <Label className="sm:col-span-2">
          Address
          <Input value={seller.address} onChange={(e) => set({ address: e.target.value })} />
        </Label>
        <Label>
          GSTIN
          <Input className="font-mono uppercase" maxLength={15} value={seller.gstin} onChange={(e) => set({ gstin: e.target.value.toUpperCase() })} />
        </Label>
        <Label>
          SAC (service code)
          <Input inputMode="numeric" value={seller.sac} onChange={(e) => set({ sac: e.target.value })} />
        </Label>
      </fieldset>
      {canEdit ? (
        <Button variant="primary" className="self-start" onClick={save}>
          Save seller details
        </Button>
      ) : (
        <p className="text-sm text-muted-foreground">Only super admins change these.</p>
      )}
    </Card>
  );
}
